import { Trash } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "src/components/dialogue/radix-dialog";
import Search from "src/components/input/Search";
import { DatePicker } from "src/components/ui/DatePicker";
import { AttendanceType } from "src/redux/types/_attendance.type";
import { useDebounce } from "src/utils/useDebounce";
import { useAttendanceByEmployeeCode } from "./hooks/useAttendanceByEmployeeCode";
import { PDFDownloadLink } from "@react-pdf/renderer";
import AttendancePDF from "./AttendancePDF";

type Props = {
  children: React.ReactNode;
};

const getSummary = (data: AttendanceType[]) => {
  const total = data.length;
  const present = data.filter((d) => d.attendance_status === "FULLDAY").length;
  const absent = data.filter((d) => d.attendance_status === "ABSENT").length;
  const halfday = data.filter((d) => d.attendance_status === "HALFDAY").length;

  return { total, present, absent, halfday };
};

const normalizeDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const GetinExcelModal = ({ children }: Props) => {
  const [employeeCode, setEmployeeCode] = useState("");
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();

  const handleEmployeeCodeChange = (value: string) => {
    if (/^\d*$/.test(value)) {
      setEmployeeCode(value);
    }
  };

  const debouncedEmployeeCode = useDebounce(employeeCode, 500);

  const employeeId =
    debouncedEmployeeCode && !isNaN(Number(debouncedEmployeeCode))
      ? Number(debouncedEmployeeCode)
      : undefined;

  const {
    data: attendanceData = [],
    isLoading,
    isFetching,
  } = useAttendanceByEmployeeCode(employeeId);

  const filteredData = useMemo(() => {
    if (!fromDate && !toDate) return attendanceData;
    if (fromDate && toDate && toDate < fromDate) {
      return [];
    }

    return attendanceData.filter((item) => {
      const itemDate = normalizeDate(new Date(item.date));

      if (fromDate && !toDate) {
        return itemDate >= normalizeDate(fromDate);
      }

      if (fromDate && toDate) {
        return (
          itemDate >= normalizeDate(fromDate) &&
          itemDate <= normalizeDate(toDate)
        );
      }

      return true;
    });
  }, [attendanceData, fromDate, toDate]);

  const employeeInfo = useMemo(() => {
    const employee = attendanceData[0];
    const name = employee?.employee_name;
    const email = employee?.employee_email;
    const code = employee?.employee_code;
    const post = employee?.employee_post;

    return { name, email, code, post };
  }, [attendanceData]);

  const summary = getSummary(filteredData);

  const clearFilters = () => {
    setEmployeeCode("");
    setFromDate(undefined);
    setToDate(undefined);
  };

  return (
    <Dialog>
      <DialogTrigger asChild className="flex-1">
        {children}
      </DialogTrigger>

      <DialogContent className="max-h-[85vh] w-full bg-white rounded-xl max-w-7xl p-6 space-y-3">
        {/* HEADER */}
        <div>
          <h2 className="text-xl font-bold">Attendance Report</h2>
          <p className="text-sm text-slate-500">
            Detailed employee attendance overview
          </p>
        </div>

        {/* Filter */}
        <div className="flex justify-start gap-4">
          <Search
            value={employeeCode}
            placeholder="Search by Employee Code..."
            onChange={handleEmployeeCodeChange}
          />
          <DatePicker
            value={fromDate}
            onChange={setFromDate}
            placeholder="From Date"
          />
          <DatePicker
            value={toDate}
            onChange={setToDate}
            placeholder="To Date"
          />
          <button
            type="button"
            onClick={clearFilters}
            className="px-4 py-2 h-10 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
          >
            <Trash />
          </button>
        </div>
        {/* Info */}
        <div className="">
          <p>Attendance Report for: {employeeInfo.name}</p>
          <p>Email: {employeeInfo.email}</p>
          <p>Employee Code: {employeeInfo.code}</p>
          <p>Employee Role: {employeeInfo.post}</p>
          <p>
            Date Range: {fromDate ? moment(fromDate).format("MMM Do YY") : ""} -{" "}
            {toDate ? moment(toDate).format("MMM Do YY") : ""}
          </p>
        </div>

        {/* SUMMARY */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Total Days", value: summary.total },
            { label: "Present Days", value: summary.present },
            { label: "Absent Days", value: summary.absent },
            { label: "Half Days", value: summary.halfday },
          ].map((item) => (
            <div key={item.label} className="p-4 rounded-xl border bg-slate-50">
              <p className="text-sm text-slate-500">{item.label}</p>
              <p className="text-lg font-bold text-center">{item.value}</p>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="overflow-auto max-h-[400px] border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Attendance Status</th>
                <th className="p-3 text-left">Check In Time</th>
                <th className="p-3 text-left">Check Out Time</th>
              </tr>
            </thead>

            <tbody>
              {isLoading || isFetching ? (
                <tr>
                  <td colSpan={6} className="text-center p-6">
                    Loading attendance...
                  </td>
                </tr>
              ) : !employeeId ? (
                <tr>
                  <td colSpan={6} className="text-center p-6 text-slate-400">
                    Enter employee code to search
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center p-6 text-slate-400">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-slate-50">
                    <td className="p-3">
                      {moment(item.date).format("MMM Do YY")}
                    </td>

                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          item.attendance_status === "FULLDAY"
                            ? "bg-green-100 text-green-600"
                            : item.attendance_status === "ABSENT"
                              ? "bg-red-100 text-red-600"
                              : "bg-yellow-100 text-yellow-600"
                        }`}
                      >
                        {item.attendance_status}
                      </span>
                    </td>

                    <td className="p-3">
                      {item.checkin_time
                        ? moment(item.checkin_time).format("LT")
                        : "-"}
                    </td>

                    <td className="p-3">
                      {item.checkout_time &&
                      item.attendance_status !== "HALFDAY"
                        ? moment(item.checkout_time).format("LT")
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ACTIONS */}
        <div className="flex justify-center gap-4 sticky bottom-0">
          <DialogClose>
            <button className="w-full font-bold text-red-400 border border-red-200 rounded-xl px-4 py-2 hover:bg-red-200">
              Cancel
            </button>
          </DialogClose>

          <PDFDownloadLink
            document={
              <AttendancePDF
                data={filteredData}
                fromDate={fromDate}
                toDate={toDate}
              />
            }
            fileName="attendance.pdf"
          >
            {({ loading }) => (
              <button className="max-w-md flex-1 flex justify-center items-center gap-2 font-bold text-white bg-primary/90 rounded-xl px-4 py-2 hover:bg-primary">
                {loading ? "Generating PDF..." : "Download PDF"}
              </button>
            )}
          </PDFDownloadLink>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GetinExcelModal;
